// src/context/AuthContext.js
//
// ═══════════════════════════════════════════════════════════════════════════
//  CHANGE LOG — 2026-09-19
//
//  THE LOGIN PAGE ALWAYS SAID "Connection error", WHATEVER WENT WRONG.
//
//  Wrong password? "Connection error."  Server actually down? "Connection
//  error."  Two completely different problems, one useless message.
//
//  WHY
//    login() called axios and did not catch anything:
//
//        const res = await axios.post(`${API}/auth/login`, { email, password });
//        if (res.data.success) { ...; return { success: true }; }
//        return { success: false, message: res.data.message };
//
//    When the password is wrong the server replies 401 WITH a proper
//    message. But axios treats any 4xx/5xx as a thrown failure, so the
//    `return { success:false, message }` line was never reached. The throw
//    sailed past Login.js's
//
//        else setError(result.message || "Invalid credentials.");
//
//    …into its catch block, which only knows one sentence:
//
//        catch (err) { setError("Connection error. Please try again."); }
//
//    So the code that would have shown the real reason has been sitting in
//    Login.js all along, unreachable.
//
//  THE FIX
//    login() now catches, and asks one question: DID THE SERVER REPLY?
//      • replied  → pass its message through (or a sensible one for the
//                   status code) → Login.js's `else` branch finally runs
//      • no reply → that genuinely IS a connection problem, and says so
//
//    Login.js needs NO changes. Its catch stays as a safety net for real
//    JavaScript errors, which is what a catch is for.
//
//  NOTHING ELSE CHANGED — the token handling, the global axios header, the
//  /auth/me check, logout, can() and isRole() are all exactly as they were.
// ═══════════════════════════════════════════════════════════════════════════

import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "https://ipanel.idigitalhub.org/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);

  // On app load — check if token exists and is valid
  useEffect(() => {
    const token = localStorage.getItem("ipanel_token");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      axios.get(`${API}/auth/me`)
        .then(res => { if (res.data.success) setUser(res.data.user); })
        .catch(() => { localStorage.removeItem("ipanel_token"); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  /**
   * ADDED 2026-09-19 — turn a failed request into a message a person can act on.
   *
   * The one question that matters: did we get a reply from the server?
   *   • err.response exists  → the server answered, it just said no. Use its
   *                            own message when it sent one; otherwise pick a
   *                            sentence that matches the status code.
   *   • err.response missing → nothing came back at all. DNS, offline, the
   *                            backend down, CORS. That is the only case that
   *                            is genuinely a "connection error".
   */
  function describeLoginError(err) {
    // No reply at all.
    if (!err.response) {
      if (err.code === "ECONNABORTED") {
        return "The server took too long to respond. Please try again.";
      }
      return "Cannot reach the server. Check your connection and try again.";
    }

    const status    = err.response.status;
    const serverMsg = err.response.data?.message;

    // The server told us why — that is always the best answer.
    if (serverMsg) return serverMsg;

    // It didn't, so say something true for the status code rather than
    // inventing a reason.
    if (status === 401) return "Invalid email or password.";
    if (status === 403) return "This account is not allowed to sign in.";
    if (status === 429) return "Too many attempts. Please wait a moment and try again.";
    if (status >= 500)  return "The server had a problem. Please try again in a moment.";
    return `Sign-in failed (HTTP ${status}).`;
  }

  // Login
  const login = async (email, password) => {
    // CHANGED 2026-09-19: wrapped in try/catch. Without this, a 401 was
    // thrown straight past Login.js's message branch into its catch, which
    // only ever printed "Connection error".
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      if (res.data.success) {
        const { token, user } = res.data;
        localStorage.setItem("ipanel_token", token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        setUser(user);
        return { success: true };
      }
      // A 200 response that still says success:false — pass its message on.
      return { success: false, message: res.data.message };
    } catch (err) {
      return { success: false, message: describeLoginError(err) };
    }
  };

  // Logout
  const logout = () => {
    localStorage.removeItem("ipanel_token");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
    window.location.href = "/";
  };

  // Check permission
  const can = (permission) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    return user.permissions?.[permission] === true;
  };

  // Check role
  const isRole = (...roles) => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, can, isRole }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
